import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Save, Plus, Trash2, Loader2, Check, Sparkles, FolderPlus, Pencil, X, Columns, GripVertical } from "lucide-react";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROGRAM_TABS = {
  "Program A": ["Region 1", "Region 2", "Region 3", "Region 4", "Region 5"],
  "Program B": ["Region 1", "Region 2", "Region 3", "Region 4", "Region 5"],
  "Program C": ["Region 1", "Region 3", "Region 4", "Region 5"],
};
const PROGRAMS = Object.keys(PROGRAM_TABS);

const statusStyles = {
  Current: { bg: "bg-slate-100 dark:bg-slate-700", text: "text-slate-600 dark:text-slate-300", border: "border-slate-200 dark:border-slate-600" },
  "New Enhancement": { bg: "bg-blue-100 dark:bg-blue-900/40", text: "text-blue-700 dark:text-blue-300", border: "border-blue-200 dark:border-blue-700" },
};

export const MappingTable = ({
  panelTestId,
  subtitleLabel,
  subtitleColor = "text-[#475569]",
  title,
  description,
  showEnhancementStatus = false,
  showProgramFilter = false,
  showTabs = true,
  accentColor = "teal",
  testIdPrefix = "",
}) => {
  const { selectedProduct } = useAuth();
  const productId = selectedProduct?.id;
  const [activeProgram, setActiveProgram] = useState("Program C");
  const tabs = showProgramFilter ? (PROGRAM_TABS[activeProgram] || PROGRAM_TABS["Program C"]) : PROGRAM_TABS["Program C"];
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [savedRows, setSavedRows] = useState({});
  const [newSectionName, setNewSectionName] = useState("");
  const [activeSection, setActiveSection] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [editSectionValue, setEditSectionValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Column management state
  const [columns, setColumns] = useState([]);
  const [showColumnManager, setShowColumnManager] = useState(false);
  const [newColumnName, setNewColumnName] = useState("");
  const [editingColumnId, setEditingColumnId] = useState(null);
  const [editColumnName, setEditColumnName] = useState("");

  const p = testIdPrefix ? `${testIdPrefix}-` : "";

  // Fetch columns
  const fetchColumns = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mapping-columns`);
      setColumns(res.data);
    } catch (err) {
      console.error("Failed to fetch columns:", err);
    }
  }, []);

  useEffect(() => {
    fetchColumns();
  }, [fetchColumns]);

  const fetchRows = useCallback(async (tab) => {
    setLoading(true);
    try {
      let params = showProgramFilter ? `?program=${activeProgram}` : "";
      if (productId) {
        params += params ? `&product_id=${productId}` : `?product_id=${productId}`;
      }
      const res = await axios.get(`${API}/mappings/${tab}${params}`);
      setRows(res.data);
    } catch (err) {
      console.error("Failed to fetch mappings:", err);
    }
    setLoading(false);
  }, [activeProgram, showProgramFilter, productId]);

  useEffect(() => { fetchRows(activeTab); }, [activeTab, fetchRows, productId]);

  // Reset tab when program changes
  useEffect(() => {
    if (showProgramFilter) {
      const newTabs = PROGRAM_TABS[activeProgram] || PROGRAM_TABS["Program C"];
      if (!newTabs.includes(activeTab)) {
        setActiveTab(newTabs[0]);
      } else {
        fetchRows(activeTab);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProgram]);

  const handleCellChange = (rowId, field, value) => {
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, [field]: value } : r)));
  };

  const saveRow = async (row) => {
    setSaving((prev) => ({ ...prev, [row.id]: true }));
    try {
      const payload = {
        section: row.section, cas_label: row.cas_label, cas_api_field: row.cas_api_field,
        sf_object: row.sf_object, sf_field: row.sf_field,
      };
      if (row.custom_fields && Object.keys(row.custom_fields).length > 0) {
        payload.custom_fields = row.custom_fields;
      }
      if (showEnhancementStatus) payload.enhancement_status = row.enhancement_status;
      await axios.patch(`${API}/mappings/${row.id}`, payload);
      setSavedRows((prev) => ({ ...prev, [row.id]: true }));
      setTimeout(() => setSavedRows((prev) => ({ ...prev, [row.id]: false })), 1500);
    } catch (err) {
      console.error("Failed to save:", err);
    }
    setSaving((prev) => ({ ...prev, [row.id]: false }));
  };

  const addRowToSection = async (groupTitle) => {
    try {
      const payload = { group_title: groupTitle, section: "", cas_label: "", cas_api_field: "", sf_object: "Lead", sf_field: "" };
      if (showEnhancementStatus) payload.enhancement_status = "New Enhancement";
      let params = showProgramFilter ? `?program=${activeProgram}` : "";
      if (productId) {
        params += params ? `&product_id=${productId}` : `?product_id=${productId}`;
      }
      const res = await axios.post(`${API}/mappings/${activeTab}${params}`, payload);
      setRows((prev) => [...prev, res.data]);
    } catch (err) {
      console.error("Failed to add row:", err);
    }
  };

  const createNewSection = async () => {
    const name = newSectionName.trim();
    if (!name) return;
    try {
      const payload = { group_title: name, section: "", cas_label: "", cas_api_field: "", sf_object: "Lead", sf_field: "" };
      if (showEnhancementStatus) payload.enhancement_status = "New Enhancement";
      let params = showProgramFilter ? `?program=${activeProgram}` : "";
      if (productId) {
        params += params ? `&product_id=${productId}` : `?product_id=${productId}`;
      }
      const res = await axios.post(`${API}/mappings/${activeTab}${params}`, payload);
      setRows((prev) => [...prev, res.data]);
      setNewSectionName("");
    } catch (err) {
      console.error("Failed to create section:", err);
    }
  };

  const deleteRow = async (rowId) => {
    try {
      await axios.delete(`${API}/mappings/${rowId}`);
      setRows((prev) => prev.filter((r) => r.id !== rowId));
    } catch (err) {
      console.error("Failed to delete:", err);
    }
  };

  const renameSection = async (oldName, newName) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName) { setEditingSection(null); return; }
    const affectedRows = rows.filter((r) => (r.group_title || "Other") === oldName);
    try {
      await Promise.all(affectedRows.map((r) => axios.patch(`${API}/mappings/${r.id}`, { group_title: trimmed })));
      setRows((prev) => prev.map((r) => (r.group_title || "Other") === oldName ? { ...r, group_title: trimmed } : r));
      if (activeSection === oldName) setActiveSection(trimmed);
    } catch (err) {
      console.error("Failed to rename section:", err);
    }
    setEditingSection(null);
  };

  // Column management functions
  const addColumn = async () => {
    const name = newColumnName.trim();
    if (!name) return;
    try {
      const res = await axios.post(`${API}/mapping-columns`, { name });
      setColumns((prev) => [...prev, res.data]);
      setNewColumnName("");
    } catch (err) {
      console.error("Failed to add column:", err);
    }
  };

  const updateColumnName = async (colId) => {
    const name = editColumnName.trim();
    if (!name) { setEditingColumnId(null); return; }
    try {
      await axios.patch(`${API}/mapping-columns/${colId}?name=${encodeURIComponent(name)}`);
      setColumns((prev) => prev.map((c) => c.id === colId ? { ...c, name } : c));
    } catch (err) {
      console.error("Failed to update column:", err);
    }
    setEditingColumnId(null);
  };

  const deleteColumn = async (colId) => {
    try {
      await axios.delete(`${API}/mapping-columns/${colId}`);
      setColumns((prev) => prev.filter((c) => c.id !== colId));
    } catch (err) {
      console.error("Failed to delete column:", err);
      alert(err.response?.data?.detail || "Cannot delete this column");
    }
  };

  // Get cell value for a row and column
  const getCellValue = (row, col) => {
    if (col.key in row) return row[col.key] || "";
    return row.custom_fields?.[col.key] || "";
  };

  // Set cell value for a row and column
  const handleDynamicCellChange = (rowId, col, value) => {
    setRows((prev) => prev.map((r) => {
      if (r.id !== rowId) return r;
      if (col.key in r) {
        return { ...r, [col.key]: value };
      }
      return { ...r, custom_fields: { ...(r.custom_fields || {}), [col.key]: value } };
    }));
  };

  // Auto-save a row when user leaves an input field
  const handleBlurSave = useCallback((rowId) => {
    const row = rows.find((r) => r.id === rowId);
    if (row) saveRow(row);
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const isBlue = accentColor === "blue";
  const clearBtnClass = isBlue
    ? "bg-blue-600 text-white hover:bg-blue-700"
    : "bg-[#00D4C8] text-white hover:bg-[#00bfb5]";
  const filterRingClass = isBlue
    ? "focus:ring-blue-500/30 focus:border-blue-500"
    : "focus:ring-[#00D4C8]/30 focus:border-[#00D4C8]";
  const createBtnClass = isBlue
    ? "bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
    : "bg-[#0F172A] hover:bg-slate-800 text-white shadow-sm";
  const dashedBorderClass = isBlue ? "border-blue-300" : "border-slate-300";
  const folderIconClass = isBlue ? "text-blue-400" : "text-slate-400";
  const addRowHoverClass = isBlue ? "hover:text-blue-600" : "hover:text-slate-600";

  const [statusFilter, setStatusFilter] = useState("");

  const currentCount = rows.filter((r) => r.enhancement_status === "Current").length;
  const enhancementCount = rows.filter((r) => r.enhancement_status === "New Enhancement").length;
  const sectionNames = [...new Set(rows.map((r) => r.group_title || "Other"))];

  const filteredRows = showEnhancementStatus && statusFilter
    ? rows.filter((r) => r.enhancement_status === statusFilter)
    : rows;

  const sections = filteredRows.reduce((acc, row) => {
    const sec = row.group_title || "Other";
    if (activeSection && sec !== activeSection) return acc;
    if (!acc[sec]) acc[sec] = [];
    acc[sec].push(row);
    return acc;
  }, {});

  return (
    <>
    <div data-testid={panelTestId}>
      <p className={`text-xs font-semibold tracking-widest uppercase ${subtitleColor} mb-3`}>{subtitleLabel}</p>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 dark:text-slate-100 font-['Outfit']">{title}</h1>
      <p className="text-base text-slate-500 dark:text-slate-400 mt-2">{description}</p>

      <Separator className="my-8 bg-slate-200 dark:bg-slate-700" />

      {/* Program selector */}
      {showProgramFilter && (
        <div className="flex items-center gap-2 mb-5" data-testid={`${p}program-selector`}>
          {PROGRAMS.map((prog) => (
            <button key={prog} onClick={() => { setActiveProgram(prog); setActiveSection(null); setStatusFilter(""); }} data-testid={`${p}program-${prog}`}
              className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${activeProgram === prog ? "bg-[#0F172A] dark:bg-slate-200 text-white dark:text-slate-900 shadow-md" : "bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:text-slate-700 dark:hover:text-slate-200"}`}>
              {prog}
            </button>
          ))}
        </div>
      )}

      {/* Tab bar */}
      {showTabs && (
        <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid={`${p}tabs`}>
          {tabs.map((tab) => (
            <button key={tab} onClick={() => { setActiveTab(tab); setActiveSection(null); }} data-testid={`${p}tab-${tab}`}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${activeTab === tab ? "bg-[#0F172A] dark:bg-slate-200 text-white dark:text-slate-900 shadow-sm" : "bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:text-slate-700 dark:hover:text-slate-200"}`}>
              {tab}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            {showEnhancementStatus ? (
              <>
                <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">{currentCount} Current</Badge>
                <Badge className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/40"><Sparkles className="w-3 h-3 mr-1" />{enhancementCount} New</Badge>
              </>
            ) : (
              <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">{rows.length} fields</Badge>
            )}
          </div>
        </div>
      )}

      {/* Status counts when tabs are hidden */}
      {!showTabs && (
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            {showEnhancementStatus ? (
              <>
                <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">{currentCount} Current</Badge>
                <Badge className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/40"><Sparkles className="w-3 h-3 mr-1" />{enhancementCount} New</Badge>
              </>
            ) : (
              <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">{rows.length} fields</Badge>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowColumnManager(!showColumnManager)}
            className="text-xs dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            <Columns className="w-3.5 h-3.5 mr-1.5" />
            Manage Columns
          </Button>
        </div>
      )}

      {/* Column Manager */}
      {showColumnManager && (
        <Card className="border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/20 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Column Manager</h3>
              <button onClick={() => setShowColumnManager(false)} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2 mb-4">
              {columns.map((col) => (
                <div key={col.id} className="flex items-center gap-2 bg-white dark:bg-slate-800 p-2 rounded-lg border border-slate-200 dark:border-slate-700">
                  <GripVertical className="w-4 h-4 text-slate-300 dark:text-slate-600" />
                  {editingColumnId === col.id ? (
                    <>
                      <Input
                        value={editColumnName}
                        onChange={(e) => setEditColumnName(e.target.value)}
                        className="h-7 text-xs flex-1 dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
                        autoFocus
                        onKeyDown={(e) => { if (e.key === "Enter") updateColumnName(col.id); if (e.key === "Escape") setEditingColumnId(null); }}
                      />
                      <button onClick={() => updateColumnName(col.id)} className="p-1 rounded hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400">
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setEditingColumnId(null)} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="text-sm text-slate-700 dark:text-slate-200 flex-1">{col.name}</span>
                      <span className="text-[10px] text-slate-400 dark:text-slate-500 font-mono">{col.key}</span>
                      <button
                        onClick={() => { setEditingColumnId(col.id); setEditColumnName(col.name); }}
                        className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                        title="Rename"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                      {col.deletable !== false && (
                        <button
                          onClick={() => setDeleteTarget({ fn: () => deleteColumn(col.id), label: col.label || "this column" })}
                          className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/30 text-slate-400 hover:text-red-500 dark:hover:text-red-400"
                          title="Delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="New column name..."
                value={newColumnName}
                onChange={(e) => setNewColumnName(e.target.value)}
                className="h-8 text-xs flex-1"
                onKeyDown={(e) => { if (e.key === "Enter") addColumn(); }}
              />
              <Button onClick={addColumn} disabled={!newColumnName.trim()} size="sm" className="h-8 bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Column
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section filter */}
      {!loading && sectionNames.length > 0 && (
        <div className="flex items-center gap-3 mb-6" data-testid={`${p}section-filters`}>
          <span className="text-xs font-medium text-slate-500">Filter by section:</span>
          <select value={activeSection || ""} onChange={(e) => setActiveSection(e.target.value || null)}
            className={`h-8 px-3 rounded-lg text-xs font-semibold border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 ${filterRingClass} cursor-pointer appearance-auto`}
            data-testid={`${p}section-filter-select`}>
            <option value="">All Sections ({sectionNames.length})</option>
            {sectionNames.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
          {activeSection && (
            <button onClick={() => setActiveSection(null)} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${clearBtnClass} transition-colors`} data-testid={`${p}section-filter-clear`}>Clear</button>
          )}
          {showEnhancementStatus && (
            <>
              <span className="text-xs font-medium text-slate-400 ml-4">|</span>
              <span className="text-xs font-medium text-slate-500">Status:</span>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className={`h-8 px-3 rounded-lg text-xs font-semibold border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 ${filterRingClass} cursor-pointer appearance-auto`}
                data-testid={`${p}status-filter-select`}>
                <option value="">All Statuses</option>
                <option value="Current">Current</option>
                <option value="New Enhancement">New Enhancement</option>
              </select>
              {statusFilter && (
                <button onClick={() => setStatusFilter("")} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${clearBtnClass} transition-colors`} data-testid={`${p}status-filter-clear`}>Clear</button>
              )}
            </>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /><span className="ml-2 text-sm text-slate-400">Loading mappings...</span></div>
      ) : (
        <>
          {Object.entries(sections).map(([sectionName, sectionRows]) => {
            const secEnhancements = showEnhancementStatus ? sectionRows.filter((r) => r.enhancement_status === "New Enhancement").length : 0;
            return (
              <Card key={sectionName} className="card-modern mb-4" data-testid={`${p}section-${sectionName}`}>
                <CardContent className="p-0">
                  <div className="px-5 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
                    {editingSection === sectionName ? (
                      <div className="flex items-center gap-2">
                        <Input value={editSectionValue} onChange={(e) => setEditSectionValue(e.target.value)} className="h-7 text-xs font-semibold w-48 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100" autoFocus data-testid={`${p}edit-section-input-${sectionName}`}
                          onKeyDown={(e) => { if (e.key === "Enter") renameSection(sectionName, editSectionValue); if (e.key === "Escape") setEditingSection(null); }} />
                        <button onClick={() => renameSection(sectionName, editSectionValue)} className="p-1 rounded hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400 transition-colors" data-testid={`${p}save-section-name-${sectionName}`}><Check className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setEditingSection(null)} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400 transition-colors"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 group/header">
                        <Badge variant="secondary" className="bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold">{sectionName}</Badge>
                        <button onClick={() => { setEditingSection(sectionName); setEditSectionValue(sectionName); }}
                          className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors opacity-0 group-hover/header:opacity-100" title="Rename section" data-testid={`${p}rename-section-${sectionName}`}>
                          <Pencil className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    <span className="text-xs text-slate-400 dark:text-slate-500">{sectionRows.length} fields</span>
                    {showEnhancementStatus && secEnhancements > 0 && (
                      <Badge className="bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 text-[10px] hover:bg-blue-50 dark:hover:bg-blue-900/40">{secEnhancements} enhancement{secEnhancements > 1 ? "s" : ""}</Badge>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-100 dark:border-slate-700">
                          {columns.map((col) => (
                            <th key={col.id} className="text-left px-3 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                              {col.name}
                            </th>
                          ))}
                          {showEnhancementStatus && <th className="text-left px-3 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider w-[145px]">Status</th>}
                          <th className="px-2 py-2.5 w-[70px]"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {sectionRows.map((row) => {
                          const st = statusStyles[row.enhancement_status] || statusStyles.Current;
                          return (
                            <tr key={row.id} className={`border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-colors group ${showEnhancementStatus && row.enhancement_status === "New Enhancement" ? "bg-blue-50/30 dark:bg-blue-900/20" : ""}`} data-testid={`${p}row-${row.id}`}>
                              {columns.map((col) => (
                                <td key={col.id} className="px-2 py-1.5">
                                  <Input 
                                    value={getCellValue(row, col)} 
                                    onChange={(e) => handleDynamicCellChange(row.id, col, e.target.value)}
                                    onBlur={() => handleBlurSave(row.id)}
                                    className="h-8 text-xs border-transparent bg-transparent hover:border-slate-200 dark:hover:border-slate-600 focus:border-slate-300 dark:focus:border-slate-500 focus:bg-white dark:focus:bg-slate-800 dark:text-slate-200" 
                                  />
                                </td>
                              ))}
                              {showEnhancementStatus && (
                                <td className="px-2 py-1.5">
                                  <Select value={row.enhancement_status} onValueChange={(val) => { handleCellChange(row.id, "enhancement_status", val); setTimeout(() => handleBlurSave(row.id), 100); }}>
                                    <SelectTrigger className={`h-8 text-xs border ${st.border} ${st.bg} ${st.text} font-semibold w-[145px]`} data-testid={`${p}status-${row.id}`}><SelectValue /></SelectTrigger>
                                    <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                                      <SelectItem value="Current" className="dark:text-slate-100 dark:hover:bg-slate-700">Current</SelectItem>
                                      <SelectItem value="New Enhancement" className="dark:text-slate-100 dark:hover:bg-slate-700">New Enhancement</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </td>
                              )}
                              <td className="px-2 py-1.5">
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button onClick={() => saveRow(row)} disabled={saving[row.id]} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-green-600 dark:hover:text-green-400 transition-colors" title="Save" data-testid={`${p}save-${row.id}`}>
                                    {saving[row.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : savedRows[row.id] ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Save className="w-3.5 h-3.5" />}
                                  </button>
                                  <button onClick={() => setDeleteTarget({ fn: () => deleteRow(row.id), label: "this row" })} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/30 text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors" title="Delete" data-testid={`${p}delete-${row.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-5 py-2.5 border-t border-slate-100 dark:border-slate-700">
                    <button onClick={() => addRowToSection(sectionName)} className={`flex items-center gap-1.5 text-xs font-medium text-slate-400 dark:text-slate-500 hover:text-${accentColor}-600 dark:hover:text-${accentColor}-400 transition-colors`} data-testid={`${p}add-row-section-${sectionName}`}>
                      <Plus className="w-3.5 h-3.5" /> Add row to {sectionName}
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          <Card className={`border border-dashed ${dashedBorderClass} dark:border-slate-600 shadow-sm bg-white dark:bg-slate-800/60 mt-4`} data-testid={`${p}new-section-card`}>
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <FolderPlus className={`w-5 h-5 ${folderIconClass} flex-shrink-0`} />
                <Input value={newSectionName} onChange={(e) => setNewSectionName(e.target.value)} placeholder="Enter new section name..." className="h-9 text-sm flex-1" data-testid={`${p}new-section-name-input`}
                  onKeyDown={(e) => { if (e.key === "Enter") createNewSection(); }} />
                <Button onClick={createNewSection} disabled={!newSectionName.trim()} className={createBtnClass} data-testid={`${p}create-section-btn`}>
                  <Plus className="w-4 h-4 mr-2" /> Create Section
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
    <ConfirmDeleteDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={() => { deleteTarget?.fn(); }} title="Delete Item" description={`Are you sure you want to delete ${deleteTarget?.label}? This action cannot be undone.`} />
    </>
  );
};